import { Trash2 } from 'lucide-react';
import { TruncatedBadge } from '@/components/ui/TruncatedBadge';

interface EnumChipPickerProps {
  /** Selected values, in the order they were added. */
  value: string[];
  /** Every value the setting accepts. */
  options: readonly string[];
  onChange: (value: string[]) => void;
  getLabel: (option: string) => string;
  /** Accessible name of the add control. */
  addLabel: string;
  /** Text the add control shows before a value is picked. */
  placeholder: string;
  disabled?: boolean;
}

/**
 * Chip list over a CLOSED set of values (a string-array setting whose item
 * pattern is an enum, e.g. KIR-3612 "Force selected policies to Enforce").
 * Unlike {@link import('./StringArrayInput').StringArrayInput} there is no free
 * text: the add control offers only the options not already picked.
 */
export function EnumChipPicker({
  value,
  options,
  onChange,
  getLabel,
  addLabel,
  placeholder,
  disabled = false,
}: EnumChipPickerProps) {
  const remaining = options.filter(option => !value.includes(option));

  return (
    <div className="flex flex-wrap items-center gap-2">
      {value.map(item => (
        <TruncatedBadge
          key={item}
          variant="secondary"
          rightContent={
            <button
              type="button"
              aria-label={`Remove ${getLabel(item)}`}
              onClick={() => onChange(value.filter(v => v !== item))}
              className="ml-1 hover:text-destructive flex-shrink-0"
              disabled={disabled}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          }
        >
          {getLabel(item)}
        </TruncatedBadge>
      ))}
      {remaining.length > 0 && (
        <select
          aria-label={addLabel}
          value=""
          disabled={disabled}
          onChange={e => {
            if (e.target.value) onChange([...value, e.target.value]);
          }}
          className="px-2 py-1 text-xs border border-dashed border-input rounded-md bg-background disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          <option value="">{placeholder}</option>
          {remaining.map(option => (
            <option key={option} value={option}>
              {getLabel(option)}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
